import React, { useEffect, useRef, useState } from 'react';
import Badge from '../Badge';
import { TbCircleCheckFilled } from 'react-icons/tb';
import SlotStepProduct from './SlotStepProduct';
import { useCartContext } from '../../../Contexts/CartContext';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext'
import { useTranslation } from 'react-i18next';

interface SlotStepProps {
  product: any;
  step: any;
  className?: string;
  stepIndex: number;
  scrollToNextStepWithOffset?: CallableFunction;
}

const SlotStep = ({
  product,
  step,
  className,
  stepIndex,
  scrollToNextStepWithOffset = () => {},
}: SlotStepProps) => {

  const { t } = useTranslation();
  const { getTotalSelectedComplements, cartDisplay } = useCartContext();
  const { isStoreCounter } = useOrderTypeContext();
  const [isRequired, setIsRequired] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isQuantityOk, setIsQuantityOk] = useState(false);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    setIsRequired(step.required && getTotalSelectedComplements(step.id) < step.minimum_quantity);
    setIsComplete(step.required && getTotalSelectedComplements(step.id) >= step.minimum_quantity);
    setIsQuantityOk(getTotalSelectedComplements(step.id) >= step.minimum_quantity);
  }, [getTotalSelectedComplements, step]);

  useEffect(() => {

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    const isQuantityValid = getTotalSelectedComplements(step.id) === step.quantity;
    const nextElement = document.getElementById(`step_${stepIndex + 1}`);

    if (!isQuantityValid || !nextElement) return;

    scrollToNextStepWithOffset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const getStepMessage = () => {
    const { minimum_quantity, quantity, required } = step;

    if (minimum_quantity && !required) {
      return `Escolha pelo menos ${minimum_quantity} ${minimum_quantity > 1 ? t('layout.item_label_plural') : t('layout.item_label_singular')}`;
    }

    if (required && minimum_quantity && minimum_quantity !== quantity) {
      return `Escolha de ${minimum_quantity} até ${quantity}`;
    }

    return `Escolha ${quantity} ${quantity > 1 ? 'itens' : 'item'}`;
  };


  return (
    <div id={`step_${stepIndex}`} className='z-50 flex bg-white flex-col md:ring-1 ring-gray-100 rounded-lg  md:overflow-hidden'>


      <div className='sticky top-0 z-40 flex flex-col p-4 pb-3 w-full gap-1 border-t border-gray-150 bg-gradient-to-b from-gray-100 to-white/90 md:shadow-none' style={{ willChange: 'transform' }}>

          <div className='flex justify-between items-center text-left text-md'>
            <h4 className='text-xl text-shopfood-title font-bold leading-5'>{step.name}</h4>

            <div className='flex items-center gap-0.5 self-start'>
              {(!isStoreCounter || (isStoreCounter && cartDisplay)) &&
                <>
                  {isRequired && <Badge type='required' className='text-xs animate-pulse'>{t('form.validation.required')}</Badge>}
                  {(isComplete) && <TbCircleCheckFilled size={22} className={'text-shopfood-success zoomInIcon'} />}
                  <Badge outline={!step.required} type={getTotalSelectedComplements(step.id) >= step.minimum_quantity ? 'success' : ''} className={`flex justify-center text-nowrap text-sm ${step.required && !isComplete && 'text-shopfood-title'}`}>{getTotalSelectedComplements(step.id)} / { step.quantity }</Badge>
                </>
              }
            </div>
          </div>

          {(!isStoreCounter || (isStoreCounter && cartDisplay)) &&
            <span className={`font-medium flex gap-1 leading-5 ${isQuantityOk ? 'text-shopfood-success' : 'text-gray-500'}`}>
              { getStepMessage() }
            </span>
          }

          {step.description && <span className='text-left text-gray-500 leading-5'>{ step.description }</span>}

      </div>

      <section className={`text-left gap-1 md:gap-2 ${className} grid divide-y divide-gray-100 md:ring-1 ring-inset ring-gray-50 rounded-b-lg`}>
        {step.complements.map((complement: any, index: number) =>
          <SlotStepProduct product={product} complement={complement} step={step} key={index} />
        )}
      </section>


    </div>
  )
}

export default SlotStep;